import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart, CompoundSelect, effectiveAnnual } from './_finHelpers';
import { CNetActions } from '../other/_cnet';

const DEF = { initial: 20000, annualContrib: 5000, monthlyContrib: 0, contribWhen: 'beginning', rate: 5, compound: 'annually', years: 5, months: 0, taxRate: 0, inflation: 3 };

// Interest Calculator — mirrors image: initial + annual/monthly contributions, tax, inflation
export default function InterestCalc() {
  const [initial, setInitial] = useState(DEF.initial);
  const [annualContrib, setAnnualContrib] = useState(DEF.annualContrib);
  const [monthlyContrib, setMonthlyContrib] = useState(DEF.monthlyContrib);
  const [contribWhen, setContribWhen] = useState(DEF.contribWhen);
  const [rate, setRate] = useState(DEF.rate);
  const [compound, setCompound] = useState(DEF.compound);
  const [years, setYears] = useState(DEF.years);
  const [months, setMonths] = useState(DEF.months);
  const [taxRate, setTaxRate] = useState(DEF.taxRate);
  const [inflation, setInflation] = useState(DEF.inflation);
  const [showResult, setShowResult] = useState(true);
  const clear = () => {
    setInitial(DEF.initial); setAnnualContrib(DEF.annualContrib); setMonthlyContrib(DEF.monthlyContrib);
    setContribWhen(DEF.contribWhen); setRate(DEF.rate); setCompound(DEF.compound); setYears(DEF.years);
    setMonths(DEF.months); setTaxRate(DEF.taxRate); setInflation(DEF.inflation);
    setShowResult(false);
  };
  const calc = () => setShowResult(true);
  const c = useMemo(() => {
    const totalMonths = years * 12 + months;
    const effAnnual = effectiveAnnual(rate, compound);
    const rMonthly = Math.pow(1 + effAnnual, 1/12) - 1;
    let bal = initial;
    const yearlyRows = [];
    let yInt = 0;
    for (let m = 1; m <= totalMonths; m++) {
      if (contribWhen === 'beginning') bal += monthlyContrib;
      if (m % 12 === 1 && contribWhen === 'beginning') bal += annualContrib;
      const i = bal * rMonthly * (1 - taxRate/100);
      bal += i;
      yInt += i;
      if (contribWhen === 'end') bal += monthlyContrib;
      if (m % 12 === 0 && contribWhen === 'end') bal += annualContrib;
      const yearDep = annualContrib + monthlyContrib * 12;
      if (m % 12 === 0 || m === totalMonths) {
        yearlyRows.push({ year: Math.ceil(m/12), deposit: (m===totalMonths?annualContrib+monthlyContrib*(m%12||12):yearDep), interest: yInt, balance: bal });
        yInt = 0;
      }
    }
    const totalContribs = annualContrib * (years + months/12) + monthlyContrib * totalMonths;
    const endBal = bal;
    const totalInt = endBal - initial - totalContribs;
    const buyingPower = endBal / Math.pow(1 + inflation/100, years + months/12);
    return { endBal, totalContribs, totalInt, buyingPower, yearlyRows };
  }, [initial, annualContrib, monthlyContrib, contribWhen, rate, compound, years, months, taxRate, inflation]);
  return (
    <CalcShell showActions={false} look="cnet" title="Interest Calculator" subtitle="Calculate compound interest growth with regular contributions, taxes, and inflation adjustment."
      about={<><p>An <b>interest calculator</b> shows how principal grows via compound interest. Adding regular deposits accelerates growth. Taxes and inflation reduce the real return.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Initial investment</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual contribution</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={annualContrib} onChange={e=>setAnnualContrib(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Monthly contribution</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthlyContrib} onChange={e=>setMonthlyContrib(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Contribute at the</label>
            <div className="flex gap-4 text-sm mt-1">
              <label className="flex items-center gap-1"><input type="radio" name="cw" checked={contribWhen==='beginning'} onChange={()=>setContribWhen('beginning')}/> beginning</label>
              <label className="flex items-center gap-1"><input type="radio" name="cw" checked={contribWhen==='end'} onChange={()=>setContribWhen('end')}/> end</label>
            </div>
          </div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label>
            <CompoundSelect value={compound} onChange={setCompound} />
          </div>
          <div className="field"><label>Investment length</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={taxRate} onChange={e=>setTaxRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Inflation rate</label><div className="flex gap-2"><input type="number" step="0.01" value={inflation} onChange={e=>setInflation(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <CNetActions onCalc={calc} onClear={clear} testId="interest" />
        </div>
        <div>
          {showResult ? (<>
          <div className="result-panel mb-4"><div className="result-title">Ending balance</div><div className="result-value" data-testid="int-ending">{fmt(c.endBal)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total principal</td><td className="text-right">{fmt(initial + c.totalContribs)}</td></tr>
            <tr><td>Total contributions</td><td className="text-right">{fmt(c.totalContribs)}</td></tr>
            <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.totalInt)}</td></tr>
            <tr><td>Buying power after inflation</td><td className="text-right font-semibold">{fmt(c.buyingPower)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Initial investment',value:initial,color:'#2563eb'},{label:'Contributions',value:c.totalContribs,color:'#10b981'},{label:'Interest',value:c.totalInt,color:'#dc2626'}]} size={180}/></div>
          </>) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see the accumulation results.</div>)}
        </div>
      </div>
      {showResult && <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
        <BarChart
          data={c.yearlyRows.map(r => ({
            label: String(r.year),
            values: [
              { value: initial, color: '#2563eb' },
              { value: Math.max(0, r.balance - initial - c.yearlyRows.slice(0, r.year).reduce((a,b)=>a+b.interest,0)), color: '#10b981' },
              { value: Math.max(0, c.yearlyRows.slice(0, r.year).reduce((a,b)=>a+b.interest,0)), color: '#f97316' },
            ]
          }))}
          stack={true}
          legend={[
            { label: 'Starting principal', color: '#2563eb' },
            { label: 'Contributions', color: '#10b981' },
            { label: 'Interest', color: '#f97316' },
          ]}
        />
      </div>}
      {showResult && <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Accumulation Schedule</h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-right">Deposit</th><th className="px-3 py-2 text-right">Interest</th><th className="px-3 py-2 text-right">Ending balance</th></tr></thead>
            <tbody>
              {c.yearlyRows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.year}</td><td className="px-3 py-2 text-right">{fmt(r.deposit)}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>}
    </CalcShell>
  );
}
